import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, PermissionEnum, SelectItemProps, Currency } from '@ui/src';
import {
  TransactionSubtype,
  TransactionType,
  RiskScoreRange as RiskScoreRangeValues,
  PaymentType,
} from './types';
import { amlDictionariesApi, PermissionResponse } from './services';
import { riskLevelsApi } from '../../hoc/risk-levels/store/services';
import { Factor, RiskScoreRange } from '../../hoc/types';

export interface AmlDitionariesState {
  transactionTypes?: TransactionType[];
  transactionSubtypes?: TransactionSubtype[];
  activePermissions?: PermissionEnum[];
  factors?: Factor[];
  riskScoreRanges?: RiskScoreRangeValues[];
  currencies?: SelectItemProps[];
  baseCurrency?: Currency;
  currenciesList?: Currency[];
  paymentTypes?: PaymentType[];
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
}

const initialState: AmlDitionariesState = {};

const transformTypeName = (
  types: Response<TransactionType> | Response<TransactionSubtype>,
) => {
  const nextTypes = types.data.edges.map((item) => {
    const result = { ...item.node };

    let { name } = result;
    const nameArr = name.split('_');

    name = nameArr
      .map((word) => {
        const value = `${word.charAt(0)}${word.slice(1).toLowerCase()}`;

        return value;
      })
      .join(' ');

    result.name = name;
    result.defaulName = item.node.name;

    return result;
  });

  return nextTypes;
};

export const amlDictionariesSlice = createSlice({
  name: 'amlDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setFactors: (state, action: PayloadAction<Factor[]>) => {
      state.factors = action.payload;
    },
    setAmlCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.currencies = action.payload.map((item) => ({
        name: item.name,
        suffix: item.description,
        value: item.id,
      }));

      state.currenciesList = action.payload.map((item) => item);
    },
    setPaymentTypes: (state, action: PayloadAction<PaymentType[]>) => {
      state.paymentTypes = action.payload;
    },
    setAmlBaseCurrency: (state, action: PayloadAction<Currency>) => {
      state.baseCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlTransactionTypes.matchFulfilled,
        (state, action: PayloadAction<Response<TransactionType>>) => {
          state.transactionTypes = transformTypeName(
            action.payload,
          ) as TransactionType[];
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlCurrencies.matchFulfilled,
        (state, action: PayloadAction<Response<Currency>>) => {
          state.currencies = action.payload?.data.edges.map((item) => ({
            name: item.node.name,
            suffix: item.node.description,
            value: item.node.id,
          }));

          state.currenciesList = action.payload?.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlBaseCurrencies.matchFulfilled,
        (state, action: PayloadAction<{ data: Currency }>) => {
          state.baseCurrency = action.payload.data;
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getFactors.matchFulfilled,
        (state, action: PayloadAction<Response<Factor>>) => {
          state.factors = action.payload.data.edges.map((item) => ({
            type: item.node.type,
            calculationType: item.node.calculationType,
            id: item.node.id,
            description: item.node.description,
            weight: item.node.weight,
            factorId: item.node.factorId,
          }));
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlDictionaries.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            riskScoreRanges: Response<RiskScoreRange>;
          }>,
        ) => {
          const { riskScoreRanges } = action.payload;

          state.riskScoreRanges = riskScoreRanges.data.edges.map((item) => ({
            name: item.node.name,
            value: item.node.id,
            from: item.node.rangeStart,
            to: item.node.rangeEnd,
            description: `from ${item.node.rangeStart} to ${item.node.rangeEnd}`,
          }));
        },
      )
      .addMatcher(
        riskLevelsApi.endpoints.createUncategorized.matchFulfilled,
        (state, action: PayloadAction<{ data: RiskScoreRange }>) => {
          const { data } = action.payload;

          const nextRiskScoreRanges = ([] as RiskScoreRangeValues[]).concat(
            state.riskScoreRanges as RiskScoreRangeValues[],
          );

          state.riskScoreRanges = [
            ...nextRiskScoreRanges,
            {
              name: data.name,
              value: data.id,
              from: data.rangeStart,
              to: data.rangeEnd,
              description: `from ${data.rangeStart} to ${data.rangeEnd}`,
            },
          ];
        },
      )
      .addMatcher(
        riskLevelsApi.endpoints.updateRiskScoreRange.matchFulfilled,
        (state, action: PayloadAction<{ data: RiskScoreRange }>) => {
          const { data } = action.payload;

          state.riskScoreRanges = state.riskScoreRanges?.map((item) => {
            let result = { ...item };

            if (result.value === data.id) {
              result = {
                name: data.name,
                value: data.id,
                from: data.rangeStart,
                to: data.rangeEnd,
                description: `from ${data.rangeStart} to ${data.rangeEnd}`,
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.updateFactor.matchFulfilled,
        (state, action: PayloadAction<{ data: Factor }>) => {
          const nextFactors = state.factors?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                type: action.payload.data.type,
                calculationType: action.payload.data.calculationType,
                id: action.payload.data.id,
                description: action.payload.data.description,
                weight: action.payload.data.weight,
                factorId: action.payload.data.factorId,
              };
            }

            return result;
          });

          state.factors = nextFactors;
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlPermissions.matchFulfilled,
        (state, action: PayloadAction<{ data: PermissionResponse }>) => {
          state.activePermissions = action.payload.data.permissions;

          localStorage.setItem(
            'activePermissions',
            JSON.stringify(action.payload.data.permissions),
          );
        },
      )
      .addMatcher(
        amlDictionariesApi.endpoints.getAmlWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  setActivePermissions,
  setFactors,
  setAmlBaseCurrency,
  setAmlCurrencies,
  setPaymentTypes,
} = amlDictionariesSlice.actions;

export default amlDictionariesSlice.reducer;
